import { AppLayout } from '@/components/layout/AppLayout'
import { AtlasPage } from '@/components/ui/AtlasPage'
import { PageTitle } from '@/components/ui/PageTitle'
import { Separator } from '@/components/ui/Separator'
import { PageNumber } from '@/components/ui/PageNumber'
import { Button } from '@/components/ui/Button'
import { CARE_ACTION_ICONS, EnumIcon } from '@/lib/icons'

interface Photo {
  id: number
  url: string
  thumbnail_url: string
}

interface CareLog {
  id: number
  action: string
  action_pl: string
  notes: string | null
  performed_at: string
  photos: Photo[]
}

interface Props {
  plant: { id: number; name: string }
  care_logs: CareLog[]
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString('pl-PL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString('pl-PL', {
    day: 'numeric',
    month: 'short',
  })
}

function CareLogsIndex({ plant, care_logs }: Props) {
  // Group logs by date
  const groupedLogs: Record<string, CareLog[]> = {}
  care_logs.forEach((log) => {
    const dateKey = new Date(log.performed_at).toLocaleDateString('pl-PL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    if (!groupedLogs[dateKey]) groupedLogs[dateKey] = []
    groupedLogs[dateKey].push(log)
  })

  return (
    <AtlasPage>
      <PageTitle
        title="Dziennik pielęgnacji"
        subtitle={`Dziennik pielęgnacji — ${plant.name}`}
      />
      <Separator />

      {care_logs.length > 0 ? (
        <div className="space-y-md">
          {Object.entries(groupedLogs).map(([date, logs]) => (
            <div key={date}>
              <h3 className="font-label text-xs text-gold uppercase tracking-wider mb-sm">
                {date}
              </h3>
              <div className="space-y-xs">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-start gap-sm border-l-2 border-botanical/30 pl-sm py-xs"
                  >
                    <div className="flex-1">
                      <p className="font-label text-sm text-ink inline-flex items-center gap-xs">
                        <EnumIcon entry={CARE_ACTION_ICONS[log.action]} size={14} />
                        {log.action_pl}
                      </p>
                      {log.notes && (
                        <p className="text-xs text-ink-light italic mt-xs">{log.notes}</p>
                      )}
                      {log.photos.length > 0 && (
                        <div className="flex gap-xs mt-xs">
                          {log.photos.map((photo) => (
                            <a
                              key={photo.id}
                              href={photo.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={photo.thumbnail_url}
                                alt=""
                                className="h-12 w-12 object-cover border border-frame/30"
                              />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-frame whitespace-nowrap shrink-0">
                      {new Date(log.performed_at).toLocaleTimeString('pl-PL', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-xl">
          <p className="text-frame italic font-label">Brak wpisów w dzienniku.</p>
        </div>
      )}

      <Separator />
      <div className="flex justify-center">
        <Button href={`/plants/${plant.id}`} variant="secondary">
          Powrót do rośliny
        </Button>
      </div>

      <PageNumber number={17} />
    </AtlasPage>
  )
}

CareLogsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
export default CareLogsIndex
